import React from "react";

import {
  SortOutlined,
  SinglePointRubricOutlined,
  TableOutlined,
  RubricOutlined,
  ChecklistOutlined,
  CommentCircleRightOutlined,
  ScoreBasedAssessmentOutlined,
  StudentEvaluateOutlined,
  StudentPendingOutlined,
  EyeHiddenOutlined,
  BadgeStarOutlined,
  ApprovalTickOutlined,
  TickSmallOutlined,
  ClockOutlined,
  ClockTimerOutlined,
  CloseCircleOutlined,
} from "@toddle-edu/ds-icons";

export const ASSIGNMENT_FEED_DATE_TIME_FORMAT = "MMM DD, hh:mm a";
export const FEED_VIEW = "FEED_VIEW";
export const TABULAR_VIEW = "TABULAR_VIEW";

export const ASSESSMENT_TOOL_ICON_MAPPING = {
  RUBRIC: <RubricOutlined variant={"subtle"} size={"xxx-small"} />,
  SINGLE_POINT_RUBRIC: (
    <SinglePointRubricOutlined variant={"subtle"} size={"xxx-small"} />
  ),
  CHECKLIST: <ChecklistOutlined variant={"subtle"} size={"xxx-small"} />,
  COMMENT: <CommentCircleRightOutlined variant={"subtle"} size={"xxx-small"} />,
  SCORE: <ScoreBasedAssessmentOutlined variant={"subtle"} size={"xxx-small"} />,
  ASSESSMENT_GRADE_BOUNDARY: (
    <SortOutlined variant={"subtle"} size={"xxx-small"} />
  ),
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY: (
    <SortOutlined variant={"subtle"} size={"xxx-small"} />
  ),
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY: (
    <SortOutlined variant={"subtle"} size={"xxx-small"} />
  ),
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY: (
    <SortOutlined variant={"subtle"} size={"xxx-small"} />
  ),
  MYP_OBJECTIVE_RUBRIC: <TableOutlined variant={"subtle"} size={"xxx-small"} />,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC: (
    <TableOutlined variant={"subtle"} size={"xxx-small"} />
  ),
  DP_ASSESSMENT_RUBRIC: <TableOutlined variant={"subtle"} size={"xxx-small"} />,
  IB_GRADE_SCALE: <TableOutlined variant={"subtle"} size={"xxx-small"} />,
  SCORE_BASED_RUBRIC: <RubricOutlined variant={"subtle"} size={"xxx-small"} />,
  STANDARD_BASED_RUBRIC: (
    <RubricOutlined variant={"subtle"} size={"xxx-small"} />
  ),
  GRADE_SCALE_TOOL: <BadgeStarOutlined variant={"subtle"} size={"xxx-small"} />,
};

export const ASSIGNMENT_TASK_STATUS_MAPPING = {
  PUBLISHED: "TO_REVIEW",
  ARCHIVED: "REVIEWED",
  SCHEDULED: "SCHEDULED",
  DRAFT: "DRAFT",
  UNREAD_MESSAGES: "UNREAD",
  ALL_POSTS: "ALL_ITEMS",
};

export const ASSIGNMENT_STATE_TO_HEADER_TAB_MAPPING = {
  PUBLISHED: "TODO",
  ARCHIVED: "COMPLETED",
};

export const VISIBILITY_LABEL_AND_ICON = {
  VISIBLE: {
    icon: <StudentEvaluateOutlined variant="subtle" size="xxx-small" />,
    label: "classRoom:visible_to_students_now",
  },
  TO_BE_VISIBLE: {
    icon: <StudentPendingOutlined variant="subtle" size="xxx-small" />,
    label: "classRoom:visible_after_open_date",
  },
  PRIVATE: {
    icon: <EyeHiddenOutlined variant="subtle" size="xxx-small" />,
    label: "classRoom:visible_to_teachers_only",
  },
};

export const AI_GRADING_PROGRESS_MAPPING = {
  analyzingAssignmentCriteria: 25,
  readingStudentWork: 50,
  generatingFeedback: 75,
  preparingJustification: 75,
  completed: 100,
  PENDING: 25,
  PROCESS_STARTED: 25,
  ANALYZING_ASSIGNMENT_CRITERIA: 25,
  READING_STUDENT_WORK: 50,
  GENERATING_FEEDBACK: 75,
  PREPARING_JUSTIFICATION: 75,
  COMPLETED: 100,
  FAILED: 100,
};

export const TOOL_TYPE_TO_ELEMENT_TYPE_MAPPING = {
  RUBRIC: "RUBRIC",
  SINGLE_POINT_RUBRIC: "SINGLE_POINT_RUBRIC",
  CHECKLIST: "CHECKLIST",
  COMMENT: "COMMENT",
  SCORE: "SCORE",
  ASSESSMENT_GRADE_BOUNDARY: "ASSESSMENT_GRADE_BOUNDARY",
  MYP_OBJECTIVE_RUBRIC: "MYP_OBJECTIVES",
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC: "MYP_OBJECTIVES",
  DP_ASSESSMENT_RUBRIC: "DP_CRITERIA",
  SCORE_BASED_RUBRIC: "SCORE",
  STANDARD_BASED_RUBRIC: "SCORE",
};

export const RATED_LEARNING_GOALS_ELEMENT_TYPE = [
  "ATL",
  "BENCHMARK",
  "CONCEPT",
  "DP_ATL",
  "DP_CRITERIA",
  "DP_EE",
  "DP_LEARNER_PROFILE",
  "DP_OBJECTIVES",
  "DP_SUBJECT",
  "DP_SUBJECT_STANDARD",
  "LEARNER_PROFILE",
  "MYP_ATL",
  "MYP_LEARNER_PROFILE",
  "MYP_LEARNING_STANDARD",
  "MYP_SUBJECT",
  "UBD_ATL",
  "UBD_LEARNING_STANDARD",
  "UBD_SUBJECT",
];

export const VALID_STUDENT_SUBMISSION_TYPE = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/msword", // .doc
  "application/pdf", // pdf
  "text/csv", // csv
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "application/vnd.ms-powerpoint", // .ppt
  "image/jpeg", // .jpeg
  "image/jpg", // .jpg
  "image/png", // .png
  "image/webp", // .webp
];

export const CODE_BASED_MIME_TYPES = [
  // Web languages
  "application/javascript",
  "text/javascript",
  "application/x-typescript",
  "text/x-python-script",
  "text/html",
  "text/css",
  "application/json",
  "application/xml",
  "text/xml",

  // Backend
  "text/x-python",
  "text/x-java-source",
  "application/java",
  "text/x-go",
  "text/x-shellscript",
  "application/x-sh",
  "text/x-perl",
  "text/x-ruby",
  "text/x-lua",

  // C-family
  "text/x-c",
  "text/x-c++",
  "text/x-csharp",
  "text/x-csharp-source",

  // Config/data
  "application/x-yaml",
  "text/yaml",
  "application/toml",
  "text/toml",
  "text/markdown",

  // Others
  "text/plain",
  "text/x-makefile",
  "text/x-dockerfile",
  "application/sql",
  "text/x-sql",
  "application/x-httpd-php",
];

export const SUBMISSION_STATUS = {
  SUBMISSION_PENDING: "SUBMISSION_PENDING",
  TURNED_IN_ON_TIME: "TURNED_IN_ON_TIME",
  TURNED_IN_LATE: "TURNED_IN_LATE",
  OVERDUE: "OVERDUE",
  NOT_SUBMITTED: "NOT_SUBMITTED",
  REQUEST_RESUBMISSION: "REQUEST_RESUBMISSION",
  REQUEST_RESUBMISSION_OVERDUE: "REQUEST_RESUBMISSION_OVERDUE",
  RESUBMISSION_NOT_SUBMITTED: "RESUBMISSION_NOT_SUBMITTED",
};

export const OVERRIDE_STATUS_OPTIONS = [
  {
    value: SUBMISSION_STATUS.SUBMISSION_PENDING,
    localeLabel: "classRoom:pending",
    icon: <ClockTimerOutlined variant="subtle" size="xx-small" />,
  },
  {
    value: SUBMISSION_STATUS.TURNED_IN_ON_TIME,
    localeLabel: "classRoom:turned_in_on_time",
    icon: <TickSmallOutlined variant="success" size="xx-small" />,
  },
  {
    value: SUBMISSION_STATUS.TURNED_IN_LATE,
    localeLabel: "classRoom:turned_in_late",
    icon: <ApprovalTickOutlined variant="link" size="xx-small" />,
  },
  {
    value: SUBMISSION_STATUS.OVERDUE,
    localeLabel: "classRoom:overdue",
    icon: <ClockOutlined variant="warning" size="xx-small" />,
  },
  {
    value: SUBMISSION_STATUS.NOT_SUBMITTED,
    localeLabel: "classRoom:not_submitted",
    icon: <CloseCircleOutlined variant="critical" size="xx-small" />,
  },
];

export const DONE_STATUSES = [
  SUBMISSION_STATUS.TURNED_IN_ON_TIME,
  SUBMISSION_STATUS.TURNED_IN_LATE,
];

export const RESUBMISSION_STATUSES = [
  SUBMISSION_STATUS.REQUEST_RESUBMISSION,
  SUBMISSION_STATUS.REQUEST_RESUBMISSION_OVERDUE,
  SUBMISSION_STATUS.RESUBMISSION_NOT_SUBMITTED,
];

export const DEFAULT_OVERRIDE_CURRENT_STATUSES = [
  SUBMISSION_STATUS.SUBMISSION_PENDING,
];

export const TODO_STATUSES = [
  SUBMISSION_STATUS.SUBMISSION_PENDING,
  SUBMISSION_STATUS.OVERDUE,
  SUBMISSION_STATUS.NOT_SUBMITTED,
];

export const LOCK_NOT_APPLICABLE_SOURCE_STATUSES = [
  SUBMISSION_STATUS.SUBMISSION_PENDING,
  SUBMISSION_STATUS.OVERDUE,
];

export const LOCK_STATE = {
  LOCKED: "LOCKED",
  UNLOCKED: "UNLOCKED",
  MIXED: "MIXED",
};

export const EVAL_STATUS = {
  EVAL_COMPLETE: "EVAL_COMPLETE",
  EVAL_APPLICABLE: "EVAL_APPLICABLE",
};
